import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import Spinner from "./Spinner";

interface MonthRow {
  id: string;
  month: string; // YYYY-MM
  unit_price: number | null;
  cash: number | null;
  units: number | null;
  transaction_count: number;
}

interface Draft {
  month: string;
  unit_price: string;
  cash: string;
  units: string;
}

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(m: string) {
  const [y, mo] = m.split("-").map(Number);
  return y && mo ? new Date(y, mo - 1, 1).toLocaleString("en-US", { month: "short", year: "numeric" }) : m;
}

function money(n: number | null) {
  if (n === null || n === undefined) return "-";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function num(n: number | null, digits = 2) {
  if (n === null || n === undefined) return "-";
  return n.toLocaleString("en-US", { maximumFractionDigits: digits });
}

function errorText(e: any): string {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => `${(d.loc ?? []).filter((l: any) => l !== "body").join(".") || "form"}: ${String(d.msg).replace(/^Value error, /, "")}`)
      .join("\n");
  }
  if (typeof detail === "string") return detail;
  return "Request failed. Please try again.";
}

const str = (n: number | null) => (n === null || n === undefined ? "" : String(+n.toFixed(6)));

export default function MonthlySummaryManager() {
  const [rows, setRows] = useState<MonthRow[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editRow, setEditRow] = useState<MonthRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<MonthRow[]>("/monthly-summary");
      setRows(data);
    } catch (e) {
      setRows([]);
      setError(errorText(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openForm = (row?: MonthRow) => {
    setError(null);
    setNotice(null);
    setEditRow(row ?? null);
    if (row) {
      setDraft({ month: row.month, unit_price: str(row.unit_price), cash: str(row.cash), units: str(row.units) });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // New months usually only set a unit price; start from the latest one.
      setDraft({ month: thisMonth(), unit_price: str(rows?.[0]?.unit_price ?? null), cash: "0", units: "0" });
    }
  };

  const closeForm = () => { setDraft(null); setEditRow(null); setError(null); };

  const save = async () => {
    if (!draft) return;
    if (!draft.month || !(Number(draft.unit_price) > 0)) {
      setError("Please choose a month and enter a unit price above 0.");
      return;
    }
    const body = {
      month: draft.month,
      unit_price: Number(draft.unit_price),
      cash: Number(draft.cash || 0),
      units: Number(draft.units || 0),
    };
    setBusy(true);
    setError(null);
    try {
      if (editRow) {
        const { data } = await api.put<MonthRow>(`/monthly-summary/${editRow.id}`, body);
        setNotice(`Updated ${monthLabel(data.month)}.`);
      } else {
        const { data } = await api.post<MonthRow>("/monthly-summary", body);
        setNotice(`Added ${monthLabel(data.month)} with unit price ${num(data.unit_price, 4)}.`);
      }
      closeForm();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: MonthRow) => {
    if (row.transaction_count) {
      setNotice(null);
      setError(`${monthLabel(row.month)} has ${row.transaction_count} transaction(s), and its totals come from them. Edit it instead.`);
      return;
    }
    if (!window.confirm(`Delete the ${monthLabel(row.month)} row (unit price ${num(row.unit_price, 4)})?\n\nThis can't be undone.`)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.delete(`/monthly-summary/${row.id}`);
      setNotice(`Deleted ${monthLabel(row.month)}.`);
      if (editRow?.id === row.id) closeForm();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.card}>
      <div style={S.header}>
        <h3 style={S.title}>Monthly NAV {rows && <span style={S.count}>({rows.length} months)</span>}</h3>
        {!draft && <button style={S.primaryBtn} onClick={() => openForm()}>Add month</button>}
      </div>

      {draft && (
        <div style={S.form}>
          <label style={S.field}>
            <span style={S.label}>Month</span>
            <input style={S.input} type="month" value={draft.month} onChange={(e) => setDraft({ ...draft, month: e.target.value })} />
          </label>
          <label style={S.field}>
            <span style={S.label}>Unit price</span>
            <input style={S.input} type="number" min="0" step="0.0001" inputMode="decimal" value={draft.unit_price}
              onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })} />
          </label>
          <label style={S.field}>
            <span style={S.label}>Deposits total ($)</span>
            <input style={S.input} type="number" min="0" step="0.01" inputMode="decimal" value={draft.cash}
              onChange={(e) => setDraft({ ...draft, cash: e.target.value })} />
          </label>
          <label style={S.field}>
            <span style={S.label}>Units total</span>
            <input style={S.input} type="number" min="0" step="0.01" inputMode="decimal" value={draft.units}
              onChange={(e) => setDraft({ ...draft, units: e.target.value })} />
          </label>
          <div style={S.formActions}>
            <button style={{ ...S.primaryBtn, ...(busy ? S.disabled : {}) }} disabled={busy} onClick={save}>
              {busy ? "Saving…" : editRow ? "Save changes" : "Add month"}
            </button>
            <button style={S.secondaryBtn} disabled={busy} onClick={closeForm}>Cancel</button>
          </div>
          <p style={S.hint}>
            The newest month's unit price is the fund's current unit price. Months with deposits are updated
            automatically when transactions are added, edited or deleted
            {editRow?.transaction_count ? `; ${monthLabel(editRow.month)} has ${editRow.transaction_count} transaction(s), so later changes to them adjust the totals you save here` : ""}.
          </p>
        </div>
      )}

      {notice && <div style={S.success}>{notice}</div>}
      {error && <pre style={S.error}>{error}</pre>}

      {rows === null ? <Spinner /> : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={S.table}>
            <thead>
              <tr>{["Month", "Unit price", "Deposits total", "Units total", "Transactions", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} style={{
                  ...(i % 2 ? { background: "rgba(255,255,255,0.02)" } : {}),
                  ...(editRow?.id === r.id ? { background: "rgba(29,158,117,0.10)" } : {}),
                }}>
                  <td style={{ ...S.td, fontWeight: 500 }}>
                    {monthLabel(r.month)}
                    {i === 0 && <span style={{ ...S.count, fontSize: 11, marginLeft: 8 }}>current price</span>}
                  </td>
                  <td style={S.td}>{num(r.unit_price, 4)}</td>
                  <td style={S.td}>{money(r.cash)}</td>
                  <td style={S.td}>{num(r.units)}</td>
                  <td style={{ ...S.td, color: "var(--text-secondary)" }}>{r.transaction_count || "-"}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    <button style={S.rowBtn} disabled={busy} onClick={() => openForm(r)}>Edit</button>
                    <button
                      style={{ ...S.rowBtn, ...S.deleteBtn, ...(r.transaction_count ? S.disabled : {}) }}
                      disabled={busy}
                      title={r.transaction_count ? "Months with transactions can't be deleted" : undefined}
                      onClick={() => remove(r)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={6}>No months yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 20px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 12, flexWrap: "wrap", marginBottom: 16,
  },
  headerActions: { display: "flex", gap: 12, flexWrap: "wrap" },
  search: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "8px 12px",
    color: "var(--text-primary)", fontSize: 13, minWidth: 220,
  },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)" },
  count: { color: "var(--text-muted)", fontWeight: 400 },
  form: {
    display: "grid", gap: 16, marginBottom: 20, padding: 16, borderRadius: 8,
    background: "rgba(255,255,255,0.03)",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 11, fontWeight: 500, letterSpacing: "0.08em",
    textTransform: "uppercase", color: "var(--text-muted)",
  },
  input: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "10px 12px",
    color: "var(--text-primary)", fontSize: 14, width: "100%",
  },
  readOnly: { opacity: 0.6, cursor: "not-allowed" },
  formActions: { display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" },
  hint: { gridColumn: "1 / -1", fontSize: 12, color: "var(--text-muted)" },
  primaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: "var(--teal)", color: "#fff",
  },
  secondaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.2)", color: "var(--text-primary)",
  },
  disabled: { opacity: 0.4, cursor: "not-allowed" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 },
  th: {
    textAlign: "left", padding: "10px 12px",
    color: "var(--text-muted)", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    borderBottom: "1px solid rgba(255,255,255,0.07)", whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px", color: "var(--text-primary)",
    borderBottom: "1px solid rgba(255,255,255,0.04)", whiteSpace: "nowrap",
  },
  rowBtn: {
    padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.15)", color: "var(--text-primary)", marginLeft: 6,
  },
  deleteBtn: { borderColor: "rgba(224,90,90,0.4)", color: "#f08a8a" },
  success: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(29,158,117,0.12)", border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 13,
  },
  error: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(224,90,90,0.12)", border: "1px solid rgba(224,90,90,0.4)",
    color: "#f08a8a", fontSize: 13, fontFamily: "var(--font-body)", whiteSpace: "pre-wrap",
  },
};
